import React, { useMemo } from 'react';
import cn from 'classnames';
import { Select, AtsSkeleton } from '_atoms';
import type { SelectOption } from '_atoms/Select/types';
import type { PassingStepWidget } from 'actions/analyticActions';
import Empty from 'dist/images/common/icons/empty-analytics.jpg';
import { MixedCharts } from './mixed-charts';
import { AnalyticsEmpty } from '../AnalyticsEmpty/analytics-empty';
import './passing-step.scss';

export interface PassingStepProps {
  options: PassingStepWidget[];
  from: PassingStepWidget | Record<string, never>;
  to: PassingStepWidget | Record<string, never>;
  setTo: (data: PassingStepWidget) => void;
  setFrom: (data: PassingStepWidget) => void;
  loading: boolean;
}

export const PassingStep = React.memo(({ options, from, to, setTo, setFrom, loading }: PassingStepProps) => {
  const changeValue = (data: PassingStepWidget, type: 'from' | 'to') => {
    if (type === 'from') {
      if (data.index === (from as PassingStepWidget).index) return;
      setFrom(data);
    } else {
      if (data.index <= (from as PassingStepWidget).index || data.index === (to as PassingStepWidget).index) return;
      setTo(data);
    }
  };

  const totalTimeHandler = (value?: number) => {
    let result: number;
    if (value) result = value;
    else {
      result = options
        .filter(
          item => item.index >= (from as PassingStepWidget).index && item.index <= (to as PassingStepWidget).index
        )
        .reduce((sum, current) => sum + current.data, 0);
    }
    let days = '';
    let hours = '';
    if (result > 24) {
      days = `${Math.floor(result / 24)}d`;
      hours = `${result % 24}h`;
    } else {
      days = '';
      hours = `${result}h`;
    }

    return days + hours;
  };

  const yData = (item: PassingStepWidget) => {
    if (item.index === (from as PassingStepWidget).index) return item.data;
    return options
      .filter((option, index) => index <= item.index && index >= (from as PassingStepWidget).index)
      .reduce((sum, current) => sum + current.data, 0);
  };

  const areaData = useMemo(
    () => ({
      tooltip: options.map(item => ({
        name: item.label,
        data: [
          { name: 'Time:', data: totalTimeHandler(yData(item)) },
          {
            name: 'Steps:',
            data:
              item.label === (from as PassingStepWidget).label
                ? (from as PassingStepWidget).label
                : `${(from as PassingStepWidget).label} - ${item.label}`,
          },
        ],
      })),
      data: options.map(item => {
        const nothing = item.index < (from as PassingStepWidget).index || item.index > (to as PassingStepWidget).index;
        if (nothing) return { x: item.index, y: null };
        return {
          x: item.index,
          y: yData(item),
        };
      }),
    }),
    [options, from, to]
  );

  const barData = useMemo(
    () => ({
      tooltip: options.map(item => ({
        name: item.label,
        data: [
          { name: 'Time:', data: totalTimeHandler(item.data) },
          { name: 'Step:', data: item.label },
        ],
      })),
      data: options.map(item => item.data),
    }),
    [options]
  );

  const totalTime = useMemo(() => totalTimeHandler(), [options, from, to]);

  const fromOptions = useMemo(() => options?.filter(item => item.index < (to as PassingStepWidget).index), [to, from]);

  const toOptions = useMemo(() => options?.filter(item => item.index > (from as PassingStepWidget).index), [to, from]);

  const empty = useMemo(() => !!options.filter(item => !!item.data).length, [options]);

  return (
    <div id="step" className={cn('passing-step shadow', { loading })}>
      {loading ? <AtsSkeleton height={30} width={217} /> : <span className="title">Time of passing steps</span>}
      <div className="select-block">
        {loading ? (
          <>
            <AtsSkeleton height={60} width={270} />
            <AtsSkeleton height={60} width={270} />
          </>
        ) : empty ? (
          <>
            <Select
              label="From step"
              value={from}
              options={fromOptions as unknown as SelectOption[]}
              onChange={data => changeValue(data as PassingStepWidget, 'from')}
            />
            <Select
              label="To step"
              value={to}
              options={toOptions as unknown as SelectOption[]}
              onChange={data => changeValue(data as PassingStepWidget, 'to')}
            />
          </>
        ) : null}
      </div>
      {!loading && empty ? (
        <div className="total-legend">
          <div className="total">
            Total time: <span>{totalTime}</span>
          </div>
          <div className="legends">
            <div>
              <span className="line" />
              Total time
            </div>
            <div>
              <span className="bar" />
              Step time
            </div>
          </div>
        </div>
      ) : null}
      {loading ? (
        <AtsSkeleton height={282} />
      ) : empty ? (
        <MixedCharts areaData={areaData} barData={barData} />
      ) : (
        <AnalyticsEmpty text="There is no Time of passing steps" img={Empty} />
      )}
    </div>
  );
});
